import React, { useEffect, useState } from 'react'
import moment from 'moment'

import history from 'utils/history'
import { useSelector } from 'react-redux'
import jwtDecode from 'jwt-decode'
import { Scopes } from 'utils/authUtils'
import { listUsers } from 'services/apiKYE'
import { toastifyAxiosError } from 'utils/errorReporter'
import { NoDataWrapper } from 'components/ApplicationLog/BuildFeed/styles'
import { Add, CheckCircleOutline, Edit, InfoOutlined, Key, PermContactCalendar, Remove, Restore } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  TextField,
} from '@mui/material'
import _ from 'lodash'
import { USER_ROLE_FILTERS, USER_ROLE_LABELS } from 'utils/constants/user'
import ConfigUserDialog from './ConfigUserDialog'
import { ROUTE_CONSTANTS } from '../../utils/constants/routeConstants'
import styles from './styles'

const UserManagement = () => {
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)
  const [openConfigUserDialog, setOpenConfigUserDialog] = useState(false)
  const [refreshUsersData, setRefreshUsersData] = useState(false)
  const [loading, setLoading] = useState(false)
  const [usersData, setUsersData] = useState({ items: [], total: 0, page: 1, size: 10 })
  const [filters, setFilters] = useState({ page: 1, name: '', user_role: USER_ROLE_FILTERS.all })
  const [selectedUser, setSelectedUser] = useState(null)

  const callListUsers = () => {
    const user_role = filters.user_role === USER_ROLE_FILTERS.all ? null : filters.user_role
    setLoading(true)
    listUsers(token, { ...filters, user_role: user_role })
      .then((res) => {
        setUsersData(res)
      })
      .catch((err) => {
        toastifyAxiosError(err, 'Ocorreu um erro ao listar os usuários')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const decoded = jwtDecode(token)
    if (decoded.scopes.includes(Scopes.USER_MANAGEMENT_LIST_USERS) === false) {
      history.push(ROUTE_CONSTANTS.HOME)
      history.go(0)
    }
  }, [token])

  useEffect(() => {
    callListUsers()
  }, [filters.user_role, filters.page, refreshUsersData])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      callListUsers()
    }, 400)

    return () => clearTimeout(delayDebounceFn)
  }, [filters.name])

  const onClickAdd = () => {
    setSelectedUser({})
    setOpenConfigUserDialog(true)
  }

  const onCloseConfigUserDialog = () => {
    setOpenConfigUserDialog(false)
    setSelectedUser({})
  }

  const onSuccessConfigUser = () => {
    onCloseConfigUserDialog()
    setRefreshUsersData(!refreshUsersData)
  }

  const renderActive = (user) => (
    <b style={{ color: user.enabled ? 'green' : 'red', fontWeight: 600 }}>{user.enabled ? 'Ativo' : 'Inativo'}</b>
  )

  const renderLastLogin = (lastLoginAt) => (
    lastLoginAt && moment(lastLoginAt).isValid()
      ? moment(lastLoginAt).format('DD/MM/YYYY HH:mm:ss')
      : <Remove />
  )

  const renderSsoRegistration = (isRegistered) => (
    isRegistered
      ? <CheckCircleOutline aria-label="Registrado para SSO" color="success" />
      : <Remove aria-label="Não registrado para SSO" />
  )

  const renderAuthMethod = (authMethod) => {
    const authMethods = {
      "SSO": { icon: <PermContactCalendar />, label: 'SSO' },
      "Senha": { icon: <Key />, label: 'Usuário + Senha' },
      "Senha Temporária": { icon: <Restore />, label: 'Acesso Temporário' },
    }
    const method = authMethods[authMethod]

    return method ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {method.icon}
        {method.label}
      </span>
    ) : '-'
  }

  const renderEditButton = (user) => {
    const onClickEdit = () => {
      setSelectedUser(user)
      setOpenConfigUserDialog(true)
    }

    return (
      <IconButton onClick={onClickEdit}>
        <Edit fontSize="small" />
      </IconButton>
    )
  }

  const renderFilters = () => (
    <div style={{ display: 'flex', gap: 20, margin: '10px 0', alignItems: 'end', justifyContent: 'start' }}>
      <TextField
        variant="standard"
        label="Filtrar por nome ou e-mail"
        fullWidth={true}
        value={filters.name}
        onChange={(ev) => setFilters({ ...filters, name: ev.target.value, page: 1 })}
      ></TextField>
      <FormControl style={{ width: 320 }}>
        <InputLabel variant="standard" htmlFor="user-role">
          Função
        </InputLabel>
        <Select
          labelId="user-role"
          id="user-role"
          label="Função"
          variant="standard"
          value={filters.user_role}
          onChange={(ev) => setFilters({ ...filters, user_role: ev.target.value, page: 1 })}
          fullWidth={true}
        >
          {_.map(USER_ROLE_FILTERS, (role) => (
            <MenuItem key={role} value={role}>
              {USER_ROLE_LABELS[role] || role}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <div>
        <Button size="small" variant="outlined" onClick={onClickAdd} startIcon={<Add />}>
          Adicionar
        </Button>
      </div>
    </div>
  )

  return (
    <React.Fragment>
      <div className={classes.root}>
        <div className={classes.textContent}>
          <h2>Controle de acesso dos usuários</h2>
          <p>
            Nesta tela é possível visualizar, buscar e adicionar usuários do sistema, além de gerenciar permissões de
            acesso aos recursos.
          </p>
        </div>

        <div>
          <div style={{ display: 'flex', gap: 20, flexDirection: 'column', alignItems: 'center' }}>
            {renderFilters()}
            {loading ? (
              <div>
                <CircularProgress size={50} />
              </div>
            ) : !_.isEmpty(usersData?.items) ? (
              <table className={classes.dataTable}>
                <thead>
                  <tr>
                    <th>Data de criação</th>
                    <th>Nome</th>
                    <th>E-mail</th>
                    <th>Função</th>
                    <th>Modo de Autenticação</th>
                    <th>Último login</th>
                    <th>Registrado para SSO</th>
                    <th>Status</th>
                    <th>Editar</th>
                  </tr>
                </thead>

                <tbody>
                  {usersData?.items?.map((item) => (
                    <tr key={item.email}>
                      <td>{renderLastLogin(item.created)}</td>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td>{item.user_role}</td>
                      <td>{renderAuthMethod(item.auth_method)}</td>
                      <td>{renderLastLogin(item.last_login_at)}</td>
                      <td>{renderSsoRegistration(item.is_registered_in_user_pool)}</td>
                      <td>{renderActive(item)}</td>
                      <td>{renderEditButton(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <NoDataWrapper>
                <span>
                  <InfoOutlined fontSize="inherit" />
                </span>
                <span>Nenhum usuário encontrado</span>
              </NoDataWrapper>
            )}
            {!_.isEmpty(usersData?.items) && <div>
              <Pagination
                count={Math.ceil(usersData.total / usersData.size)}
                page={usersData.page}
                onChange={(__, page) => setFilters({ ...filters, page: page })}
              />
            </div>}
          </div>
        </div>
      </div>

      <ConfigUserDialog
        userData={selectedUser}
        open={openConfigUserDialog}
        onClose={onCloseConfigUserDialog}
        onSuccess={onSuccessConfigUser}
      />
    </React.Fragment>
  )
}

UserManagement.propTypes = {}

export default UserManagement
